<template>
    <!-- Admin → Informes (/admin/informes): archivos que se le cargan a un
         usuario property, en la carpeta del mes. Mismo diseño que Usuarios /
         Cupones / Property (bkCuenta + cuentaMenu + usuariosAdmin.css) e
         informesAdmin.css (prefijo ai-). Pedidos de siempre (InformeAdminController):
           GET    admin/informes/usuarios?q&todos
           GET    admin/informes?usuario_id
           POST   admin/informes          FormData: usuario_id, periodo, titulo?, archivos[]
           PUT    admin/informes/{id}     {periodo}
           DELETE admin/informes/{id}
         El archivo queda en S3 en privado; el usuario lo baja desde su sección. -->
    <div class="ai-page">
        <!-- Primera carga de la página: solo el header y la pantalla de carga -->
        <HyFLoader v-if="!usuariosListos" variant="inline" />
        <div class="bk us ai">
            <aside class="bk-side us-side ai-side">
                <router-link :to="{ name: 'admin' }" class="us-back">
                    <span aria-hidden="true" v-html="IC.back"></span>{{ $t('admin_informes.back') }}
                </router-link>
                <h1 class="bk-title">{{ $t('admin_informes.title_a') }} <span class="bk-grad">{{ $t('admin_informes.title_b') }}</span></h1>
                <p class="bk-subtitle">{{ $t('admin_informes.sub') }}</p>

                <div class="us-stats" aria-live="polite">
                    <div class="us-stat"><span class="us-stat-n">{{ numero(usuarios.length) }}</span><span class="us-stat-k">{{ $t(verTodos ? 'admin_informes.stat.found' : 'admin_informes.stat.users') }}</span></div>
                    <div class="us-stat"><span class="us-stat-n">{{ usuarioSel ? numero(informes.length) : '—' }}</span><span class="us-stat-k">{{ $t('admin_informes.stat.files') }}</span></div>
                </div>

                <!-- Usuarios -->
                <section class="ai-users-card" aria-labelledby="ai-users-t">
                    <h2 id="ai-users-t" class="ai-h">{{ $t('admin_informes.u.title') }}</h2>
                    <form class="ai-search" role="search" @submit.prevent="cargarUsuarios">
                        <label class="us-search">
                            <span aria-hidden="true" v-html="IC.search"></span>
                            <span class="us-sr">{{ $t('admin_informes.u.search_label') }}</span>
                            <input v-model="busqueda" type="search" autocomplete="off" :placeholder="$t('admin_informes.u.search_ph')" @input="buscarLuego" />
                        </label>
                    </form>
                    <label class="us-check ai-all"><input v-model="verTodos" type="checkbox" @change="cargarUsuarios" />{{ $t('admin_informes.u.all') }}</label>

                    <p v-if="cargandoUsuarios && !usuarios.length" class="ai-muted">{{ $t('admin_informes.u.loading') }}</p>
                    <div v-else-if="!usuarios.length" class="ai-muted">
                        <b>{{ $t('admin_informes.u.none') }}</b> {{ $t('admin_informes.u.none_hint') }}
                    </div>
                    <ul v-else :class="['ai-users', { 'is-cargando': cargandoUsuarios }]">
                        <li v-for="u in usuarios" :key="u.idusuario">
                            <button
                                type="button"
                                :class="['ai-user', { 'is-on': usuarioSel && usuarioSel.idusuario === u.idusuario }]"
                                :aria-pressed="String(!!(usuarioSel && usuarioSel.idusuario === u.idusuario))"
                                :aria-label="$t('admin_informes.u.pick', { n: nombre(u) })"
                                @click="seleccionar(u)"
                            >
                                <span class="ai-av" aria-hidden="true">{{ iniciales(u) }}</span>
                                <span class="ai-user-txt">
                                    <b>{{ nombre(u) }}</b>
                                    <small>{{ u.correo }}</small>
                                </span>
                                <span class="ai-user-side">
                                    <em v-if="u.tipo !== 'property'" class="ai-tag">{{ u.tipo || $t('admin_informes.u.no_type') }}</em>
                                    <span class="ai-n" :title="$tc('admin_informes.u.files', u.informes_count || 0, { n: u.informes_count || 0 })">{{ u.informes_count || 0 }}</span>
                                </span>
                            </button>
                        </li>
                    </ul>
                </section>
            </aside>

            <section ref="main" class="us-main ai-main" tabindex="-1">
                <div v-if="!usuarioSel" class="us-empty ai-pick">
                    <span aria-hidden="true" v-html="IC.folder"></span>
                    <h3>{{ $t('admin_informes.p.title') }}</h3>
                    <p>{{ $t('admin_informes.p.text') }}</p>
                </div>

                <template v-else>
                    <div class="ai-who">
                        <span class="ai-av ai-av--lg" aria-hidden="true">{{ iniciales(usuarioSel) }}</span>
                        <div class="ai-who-txt">
                            <h2>{{ nombre(usuarioSel) }}</h2>
                            <p>{{ usuarioSel.correo }} · #{{ usuarioSel.idusuario }}</p>
                        </div>
                        <span :class="['ai-badge', usuarioSel.tipo === 'property' ? 'ai-badge--on' : 'ai-badge--wait']">{{ usuarioSel.tipo || $t('admin_informes.u.no_type') }}</span>
                    </div>
                    <p v-if="usuarioSel.tipo !== 'property'" class="us-note us-note--warn ai-note"><span aria-hidden="true" v-html="IC.warn"></span><span>{{ $t('admin_informes.p.not_property', { t: usuarioSel.tipo || $t('admin_informes.u.no_type') }) }}</span></p>

                    <!-- Subir -->
                    <form class="ai-card" novalidate @submit.prevent="subir">
                        <h2 class="ai-h">{{ $t('admin_informes.up.title') }}</h2>
                        <div class="us-g2">
                            <label :class="['us-field', { 'is-invalid': !periodoValido }]">
                                <span class="us-field-l">{{ $t('admin_informes.up.month') }}</span>
                                <input v-model="periodo" type="month" required />
                                <span v-if="!periodoValido" class="us-field-e">{{ $t('admin_informes.up.bad_month') }}</span>
                            </label>
                            <label class="us-field">
                                <span class="us-field-l">{{ $t('admin_informes.up.name') }} <em>{{ $t('admin_informes.up.optional') }}</em></span>
                                <input v-model="titulo" type="text" maxlength="180" :placeholder="$t('admin_informes.up.name_ph')" />
                            </label>
                        </div>

                        <label :class="['ai-drop', { 'is-over': arrastrando }]" @dragover.prevent="arrastrando = true" @dragleave.prevent="arrastrando = false" @drop.prevent="soltarArchivos">
                            <input ref="fileInput" type="file" multiple :accept="acceptAttr" @change="elegirArchivos" />
                            <span class="ai-drop-ic" aria-hidden="true" v-html="IC.upload"></span>
                            <b>{{ $t('admin_informes.up.drop') }}</b>
                            <small>{{ $t('admin_informes.up.drop_hint') }}</small>
                        </label>

                        <ul v-if="seleccionados.length" class="ai-sel">
                            <li v-for="(a, i) in seleccionados" :key="a.name + i">
                                <span class="ai-ext" aria-hidden="true">{{ extension(a.name) }}</span>
                                <span class="ai-sel-n">{{ a.name }}</span>
                                <span class="ai-dim">{{ formatoTamano(a.size) }}</span>
                                <button type="button" class="ai-x" :aria-label="$t('admin_informes.up.remove', { n: a.name })" @click="quitarArchivo(i)"><span aria-hidden="true" v-html="IC.x"></span></button>
                            </li>
                        </ul>

                        <div class="ai-actions">
                            <button type="submit" class="bk-btn bk-btn--primary" :disabled="!seleccionados.length || !periodoValido || subiendo">
                                <span v-if="subiendo" class="bk-spinner"></span><span v-else aria-hidden="true" v-html="IC.upload"></span>{{ subiendo ? $t('admin_informes.up.uploading') : $t('admin_informes.up.submit', { m: nombreMes(periodo) }) }}
                            </button>
                        </div>

                        <p v-if="mensaje" class="ai-ok" role="status"><span aria-hidden="true" v-html="IC.check"></span>{{ mensaje }}</p>
                        <ul v-if="erroresSubida.length" class="ai-errors" role="alert">
                            <li v-for="(e, i) in erroresSubida" :key="'err-' + i"><b>{{ e.archivo }}</b>: {{ e.motivo }}</li>
                        </ul>
                    </form>

                    <!-- Carpetas -->
                    <h2 class="ai-h ai-folders-h">{{ $t('admin_informes.f.title') }}</h2>
                    <div v-if="cargandoInformes && !informes.length" class="ai-loading"><HyFLoader variant="inline" local /></div>
                    <div v-else-if="!informes.length" class="us-empty">
                        <h3>{{ $t('admin_informes.f.none_t') }}</h3>
                        <p>{{ $t('admin_informes.f.none_p') }}</p>
                    </div>
                    <div v-else :class="['ai-folders', { 'is-cargando': cargandoInformes }]">
                        <section v-for="carpeta in carpetas" :key="carpeta.periodo" class="ai-folder" :aria-label="nombreMes(carpeta.periodo)">
                            <header class="ai-folder-head">
                                <span aria-hidden="true" v-html="IC.folder"></span>
                                <b>{{ nombreMes(carpeta.periodo) }}</b>
                                <span class="ai-n">{{ $tc('admin_informes.f.files', carpeta.archivos.length, { n: carpeta.archivos.length }) }}</span>
                            </header>
                            <ul>
                                <li v-for="inf in carpeta.archivos" :key="inf.idinforme" class="ai-file">
                                    <span class="ai-ext" aria-hidden="true">{{ String(inf.extension || '').toUpperCase() }}</span>
                                    <div class="ai-file-txt">
                                        <b>{{ inf.titulo || inf.nombre_original }}</b>
                                        <small>
                                            <template v-if="inf.titulo">{{ inf.nombre_original }} · </template>{{ formatoFecha(inf.created_at) }} · {{ formatoTamano(inf.tamano) }}<template v-if="quienSubio(inf)"> · {{ $t('admin_informes.f.by', { n: quienSubio(inf) }) }}</template>
                                        </small>
                                        <form v-if="editando === inf.idinforme" class="ai-move" @submit.prevent="guardarEdicion(inf)" @keydown.esc.prevent="editando = null">
                                            <label class="us-sr" :for="`ai-mv-${inf.idinforme}`">{{ $t('admin_informes.f.move_to') }}</label>
                                            <input :id="`ai-mv-${inf.idinforme}`" ref="moverIn" v-model="edicion.periodo" type="month" />
                                            <button type="submit" class="bk-btn bk-btn--primary" :disabled="guardando"><span v-if="guardando" class="bk-spinner"></span>{{ $t('admin_informes.f.save') }}</button>
                                            <button type="button" class="bk-btn bk-btn--ghost" @click="editando = null">{{ $t('admin_informes.f.cancel') }}</button>
                                        </form>
                                    </div>
                                    <div v-if="editando !== inf.idinforme" class="ai-file-actions">
                                        <button type="button" class="bk-btn bk-btn--ghost" @click="editar(inf)"><span aria-hidden="true" v-html="IC.move"></span>{{ $t('admin_informes.f.move') }}</button>
                                        <button type="button" class="bk-btn bk-btn--ghost ai-icon-btn ai-del" :aria-label="$t('admin_informes.f.delete', { n: inf.titulo || inf.nombre_original })" :title="$t('admin_informes.f.delete', { n: inf.titulo || inf.nombre_original })" @click="eliminar(inf)"><span aria-hidden="true" v-html="IC.trash"></span></button>
                                    </div>
                                </li>
                            </ul>
                        </section>
                    </div>
                </template>
            </section>
        </div>

        <!-- Confirmación -->
        <div v-if="modal" class="us-modal">
            <div class="us-modal-bg" @click="responderModal(false)"></div>
            <div ref="modalCard" class="us-modal-card" role="alertdialog" aria-modal="true" aria-labelledby="ai-m-title" aria-describedby="ai-m-text" @keydown="teclaModal">
                <h2 id="ai-m-title">{{ modal.titulo }}</h2>
                <p id="ai-m-text">{{ modal.texto }}</p>
                <div class="us-modal-btns">
                    <button type="button" class="bk-btn bk-btn--ghost" @click="responderModal(false)">{{ $t('admin_informes.k.cancel') }}</button>
                    <button ref="modalOk" type="button" class="bk-btn us-btn--danger" @click="responderModal(true)">{{ modal.ok }}</button>
                </div>
            </div>
        </div>

        <transition name="us-toast">
            <div v-if="aviso" :class="['us-toast', `us-toast--${aviso.tipo}`]" role="status" aria-live="polite">
                <span class="us-toast-ico" aria-hidden="true">{{ aviso.tipo === 'ok' ? '✓' : '!' }}</span><span>{{ aviso.txt }}</span>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from 'axios';
import apiUrls from '@/new-constants/apiUrls';
import HyFLoader from '@/components/Loader/HyFLoader.vue';
import ICON from '@/components/Cuenta/iconos';

const P = d => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
// Íconos: texto fijo de este archivo, nunca datos del usuario.
const IC = {
    back: P('<path d="M15 18l-6-6 6-6"/>'),
    x: P('<path d="M18 6 6 18M6 6l12 12"/>'),
    check: P('<path d="M20 6 9 17l-5-5"/>'),
    warn: P('<path d="M12 3 2 20h20z"/><path d="M12 10v4M12 17h.01"/>'),
    folder: P('<path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>'),
    move: P('<path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M10 14h6M13.5 11.5 16 14l-2.5 2.5"/>'),
    search: ICON.search, upload: ICON.upload, trash: ICON.trash,
};
const EXTENSIONES = ['pdf', 'txt', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
const LOCALES = { es: 'es-CL', en: 'en-US', pt: 'pt-BR', fr: 'fr-FR', it: 'it-IT', de: 'de-DE' };
const MES_OK = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Mes en curso como 'YYYY-MM' (lo que usa <input type="month">). */
function mesActual() {
    const hoy = new Date();
    return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
}

export default {
    name: 'AdminInformes',
    components: { HyFLoader },
    data() {
        return {
            IC,
            busqueda: '', verTodos: false, usuarios: [], usuarioSel: null, informes: [],
            titulo: '', periodo: mesActual(),
            editando: null, edicion: { periodo: '' }, guardando: false,
            seleccionados: [], arrastrando: false, subiendo: false,
            cargandoUsuarios: false, cargandoInformes: false, usuariosListos: false,
            mensaje: null, erroresSubida: [],
            modal: null, aviso: null,
        };
    },
    computed: {
        loc() { return LOCALES[this.$i18n.locale] || 'es-CL'; },
        acceptAttr() { return EXTENSIONES.map(e => `.${e}`).join(','); },
        periodoValido() { return MES_OK.test(this.periodo || ''); },
        /** Archivos agrupados por carpeta mensual, de la más nueva a la más vieja. */
        carpetas() {
            const grupos = {};
            this.informes.forEach(inf => {
                const clave = inf.periodo || String(inf.created_at || '').substring(0, 7) || 'sin-mes';
                (grupos[clave] = grupos[clave] || []).push(inf);
            });
            return Object.keys(grupos).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0)).map(periodo => ({ periodo, archivos: grupos[periodo] }));
        },
    },
    created() {
        this._t = null; this._avisoT = null;
        this.cargarUsuarios();
    },
    beforeDestroy() { clearTimeout(this._t); clearTimeout(this._avisoT); },
    methods: {
        // ── Usuarios ─────────────────────────────────────────────────────
        buscarLuego() {
            clearTimeout(this._t);
            this._t = setTimeout(this.cargarUsuarios, 400);
        },
        async cargarUsuarios() {
            clearTimeout(this._t);
            const req = (this._req = (this._req || 0) + 1);
            this.cargandoUsuarios = true;
            try {
                const { data } = await axios.get(apiUrls.adminInformesUsuarios, { params: { q: this.busqueda, todos: this.verTodos ? 1 : 0 } });
                if (req === this._req) this.usuarios = data.usuarios || [];
            } catch (e) {
                console.error('[AdminInformes] no se pudieron cargar los usuarios:', e);
                if (req === this._req) { this.usuarios = []; this.avisar(this.$t('admin_informes.toast.load'), 'error'); }
            } finally {
                if (req === this._req) { this.cargandoUsuarios = false; this.usuariosListos = true; }
            }
        },
        async seleccionar(u) {
            if (this.usuarioSel && this.usuarioSel.idusuario === u.idusuario) return;
            this.usuarioSel = u;
            this.informes = [];
            this.mensaje = null;
            this.erroresSubida = [];
            this.seleccionados = [];
            this.editando = null;
            // En celular la lista queda arriba: se lleva la vista a los informes.
            if (window.innerWidth < 1024) this.$nextTick(() => this.$refs.main && this.$refs.main.scrollIntoView({ behavior: 'smooth', block: 'start' }));
            await this.cargarInformes();
        },
        async cargarInformes() {
            if (!this.usuarioSel) return;
            const id = this.usuarioSel.idusuario;
            this.cargandoInformes = true;
            try {
                const { data } = await axios.get(apiUrls.adminInformes, { params: { usuario_id: id } });
                if (!this.usuarioSel || this.usuarioSel.idusuario !== id) return;
                this.informes = data.informes || [];
                // El backend devuelve el usuario con el tipo actualizado.
                if (data.usuario) this.usuarioSel = Object.assign({}, this.usuarioSel, data.usuario);
            } catch (e) {
                console.error('[AdminInformes] no se pudieron cargar los informes:', e);
                this.informes = [];
                this.avisar(this.$t('admin_informes.toast.load'), 'error');
            } finally {
                this.cargandoInformes = false;
            }
        },

        // ── Subir ────────────────────────────────────────────────────────
        elegirArchivos(ev) {
            this.agregarArchivos(Array.from(ev.target.files || []));
            ev.target.value = ''; // permite volver a elegir el mismo archivo
        },
        soltarArchivos(ev) {
            this.arrastrando = false;
            this.agregarArchivos(Array.from(ev.dataTransfer.files || []));
        },
        agregarArchivos(archivos) {
            const rechazados = [];
            archivos.forEach(a => {
                if (EXTENSIONES.indexOf(this.extension(a.name).toLowerCase()) === -1) rechazados.push({ archivo: a.name, motivo: this.$t('admin_informes.up.bad_format') });
                else this.seleccionados.push(a);
            });
            this.erroresSubida = rechazados;
            this.mensaje = null;
        },
        quitarArchivo(i) { this.seleccionados.splice(i, 1); },
        async subir() {
            if (!this.usuarioSel || !this.seleccionados.length || !this.periodoValido || this.subiendo) return;
            this.subiendo = true;
            this.mensaje = null;
            this.erroresSubida = [];
            const fd = new FormData();
            fd.append('usuario_id', this.usuarioSel.idusuario);
            fd.append('periodo', this.periodo);
            if (this.titulo.trim()) fd.append('titulo', this.titulo.trim());
            this.seleccionados.forEach(a => fd.append('archivos[]', a));
            try {
                const { data } = await axios.post(apiUrls.adminInformes, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                const n = (data.informes || []).length;
                this.mensaje = this.$tc('admin_informes.up.done', n, { n, m: this.nombreMes(this.periodo) });
                this.erroresSubida = data.errores || [];
                this.seleccionados = [];
                this.titulo = '';
                await this.cargarInformes();
                this.cargarUsuarios();
            } catch (e) {
                console.error('[AdminInformes] falló la subida:', e);
                const d = e.response && e.response.data;
                this.erroresSubida = (d && d.errores) || [{ archivo: this.$t('admin_informes.up.title'), motivo: (d && d.message) || this.$t('admin_informes.up.failed') }];
            } finally {
                this.subiendo = false;
            }
        },

        // ── Mover / eliminar ─────────────────────────────────────────────
        editar(inf) {
            this.editando = inf.idinforme;
            this.edicion = { periodo: inf.periodo || mesActual() };
            this.$nextTick(() => { const el = this.$refs.moverIn; const i = Array.isArray(el) ? el[0] : el; if (i) i.focus(); });
        },
        async guardarEdicion(inf) {
            if (!MES_OK.test(this.edicion.periodo || '')) { this.avisar(this.$t('admin_informes.up.bad_month'), 'error'); return; }
            this.guardando = true;
            try {
                await axios.put(apiUrls.adminInformeUpdate(inf.idinforme), { periodo: this.edicion.periodo });
                this.editando = null;
                this.avisar(this.$t('admin_informes.toast.moved', { m: this.nombreMes(this.edicion.periodo) }), 'ok');
                await this.cargarInformes();
            } catch (e) {
                console.error('[AdminInformes] no se pudo mover:', e);
                this.avisar(this.$t('admin_informes.toast.move_fail'), 'error');
            } finally {
                this.guardando = false;
            }
        },
        async eliminar(inf) {
            const ok = await this.confirmar({
                titulo: this.$t('admin_informes.k.del_t'),
                texto: this.$t('admin_informes.k.del_p', { n: inf.titulo || inf.nombre_original }),
                ok: this.$t('admin_informes.k.del_ok'),
            });
            if (!ok) return;
            try {
                await axios.delete(apiUrls.adminInformeDelete(inf.idinforme));
                this.avisar(this.$t('admin_informes.toast.deleted'), 'ok');
                await this.cargarInformes();
                this.cargarUsuarios();
            } catch (e) {
                console.error('[AdminInformes] no se pudo eliminar:', e);
                this.avisar(this.$t('admin_informes.toast.del_fail'), 'error');
            }
        },

        // ── Formato ──────────────────────────────────────────────────────
        numero(n) { return Number(n || 0).toLocaleString(this.loc); },
        nombre(u) { return `${u.nombre || ''} ${u.apellido || ''}`.replace(/\s+/g, ' ').trim() || u.correo || `#${u.idusuario}`; },
        iniciales(u) { const p = this.nombre(u).split(' '); return ((p[0] || '?')[0] + (p[1] ? p[1][0] : '')).toUpperCase(); },
        extension(nombre) { return (String(nombre).split('.').pop() || '').toUpperCase(); },
        quienSubio(inf) {
            const a = inf.admin;
            if (!a) return null;
            return `${a.nombre || ''} ${a.apellido || ''}`.trim() || a.correo || null;
        },
        /** '2026-09' → 'septiembre de 2026' en el idioma del sitio. */
        nombreMes(periodo) {
            const m = /^(\d{4})-(\d{2})$/.exec(periodo || '');
            if (!m) return this.$t('admin_informes.f.no_month');
            const t = new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString(this.loc, { month: 'long', year: 'numeric' });
            return t.charAt(0).toUpperCase() + t.slice(1);
        },
        formatoFecha(v) {
            if (!v) return '';
            const d = new Date(String(v).replace(' ', 'T'));
            return isNaN(d.getTime()) ? String(v).substring(0, 10) : d.toLocaleDateString(this.loc, { day: '2-digit', month: 'short', year: 'numeric' });
        },
        formatoTamano(bytes) {
            const n = Number(bytes) || 0;
            if (n < 1024) return `${n} B`;
            if (n < 1048576) return `${(n / 1024).toFixed(0)} KB`;
            return `${(n / 1048576).toFixed(1)} MB`;
        },

        // ── Confirmación y aviso ─────────────────────────────────────────
        confirmar({ titulo, texto, ok }) {
            return new Promise(resolve => {
                this._modalFoco = document.activeElement;
                this.modal = { titulo, texto, ok, resolve };
                this.$nextTick(() => { if (this.$refs.modalOk) this.$refs.modalOk.focus(); });
            });
        },
        responderModal(v) {
            if (!this.modal) return;
            const { resolve } = this.modal;
            this.modal = null;
            const el = this._modalFoco;
            this.$nextTick(() => { if (el && document.contains(el)) el.focus(); });
            resolve(v);
        },
        teclaModal(e) {
            if (e.key === 'Escape') { e.preventDefault(); this.responderModal(false); return; }
            if (e.key !== 'Tab') return;
            const els = Array.from(this.$refs.modalCard.querySelectorAll('button'));
            const first = els[0], last = els[els.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        },
        avisar(txt, tipo) {
            clearTimeout(this._avisoT);
            this.aviso = { txt, tipo: tipo || 'ok' };
            this._avisoT = setTimeout(() => { this.aviso = null; }, 3200);
        },
    },
};
</script>

<style src="../../BankInformation/bkCuenta.css"></style>
<style src="../../Cuenta/cuentaMenu.css"></style>
<style src="../Usuarios/usuariosAdmin.css"></style>
<style src="./informesAdmin.css"></style>
